/**
 * GJESTENS DELINGER I DENNE ORGANISASJONEN (sak 232, T5) — `GET /v1/me/guest-scope`.
 *
 * Nettleseren bygger den samme `Actor` som API-et (`auth/actor.ts`), og en gjest uten `guest` får nei
 * på hver post (`guestCan`): «kan endre» på en avdeling ville ikke vist en eneste knapp. Svaret er
 * aktøren `TenantGuard` allerede bygde — avdelingene utvidet til underavdelingene — så nettleseren
 * aldri regner det ut på nytt. Tomt for et medlem.
 */
import { z } from 'zod';

import { grantPermissionSchema } from '../enums.js';
import { RESOURCE_TYPES } from '../policy/types.js';
import type { GuestScope } from '../policy/types.js';
import type { Uuid } from '../ids.js';

export const guestScopeResponseSchema = z
  .object({
    grants: z.array(
      z.object({
        id: z.uuid(),
        objectType: z.enum(RESOURCE_TYPES),
        objectId: z.uuid(),
        permission: grantPermissionSchema,
        expiresAt: z.iso.datetime({ offset: true }).nullable(),
        scopes: z.array(z.string()).nullable(),
        contactPersonId: z.uuid().nullable(),
        /** 246.3: programposten en årgangsdeling ikke tar med. Se `GuestGrant.beyondEdition`. */
        beyondEdition: z.boolean().optional(),
      }),
    ),
    departments: z.array(z.object({ departmentId: z.uuid(), permission: grantPermissionSchema })),
    editions: z.array(z.object({ editionId: z.uuid(), permission: grantPermissionSchema })),
    /* Trådene gjesten når og nivået per tråd (0469) — det `can()` svarer på en tråd fra. */
    threads: z.array(z.object({ threadId: z.uuid(), permission: grantPermissionSchema })),
    /*
     * Det en delt scene når (§5.3, 1102): `type` er postens ressurstype (programpost, vakt, ressurs,
     * reservasjon). Valgfri: et eldre API uten feltet gir ingen rekkevidde.
     */
    reach: z
      .array(z.object({ type: z.string(), id: z.uuid(), permission: grantPermissionSchema }))
      .optional(),
    /* Den tekniske advancen på programpostene scenen når, med scenedelingens nivå. */
    stageTechnical: z
      .array(z.object({ performanceId: z.uuid(), permission: grantPermissionSchema }))
      .optional(),
  })
  .meta({ id: 'GuestScopeResponse' });

export type GuestScopeResponse = z.infer<typeof guestScopeResponseSchema>;

/** Svaret som den `GuestScope` `can()` leser. */
export function guestScopeFromResponse(response: GuestScopeResponse): GuestScope {
  return {
    grants: response.grants.map((grant) => ({
      id: grant.id as Uuid,
      objectType: grant.objectType,
      objectId: grant.objectId as Uuid,
      permission: grant.permission,
      expiresAt: grant.expiresAt,
      scopes: grant.scopes,
      kind: 'external' as const,
      contactPersonId: grant.contactPersonId as Uuid | null,
      ...(grant.beyondEdition === true ? { beyondEdition: true } : {}),
    })),
    departments: new Map(
      response.departments.map((one) => [one.departmentId as Uuid, one.permission] as const),
    ),
    editions: new Map(
      response.editions.map((one) => [one.editionId as Uuid, one.permission] as const),
    ),
    threads: new Map(
      response.threads.map((one) => [one.threadId as Uuid, one.permission] as const),
    ),
    reach: new Map((response.reach ?? []).map((one) => [`${one.type}:${one.id}`, one.permission])),
    stageTechnical: new Map(
      (response.stageTechnical ?? []).map(
        (one) => [one.performanceId as Uuid, one.permission] as const,
      ),
    ),
  };
}
